/**
 * Audit Log Viewer Page - Admin + ComplianceManager
 * Sprint 1 + Sprint 11A Layout Migration
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auditAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Download,
  RefreshCw,
  AlertCircle,
  Eye,
  Filter,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const ACTIONS = [
  'CREATE', 'UPDATE', 'ARCHIVE', 'LOGIN', 'LOGOUT', 'ROLE_CHANGE', 'PERMISSION_DENIED', 'SEED'
];

const ENTITY_TYPES = ['User', 'Role', 'Client', 'AuditLogExport'];

function AuditLogViewer() {
  const navigate = useNavigate();
  const { user, canViewAuditLogs, canExportAuditLogs, loading: authLoading } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  
  // Filters
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    action: '',
    entity_type: '',
    actor_user_id: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = async () => {
    try {
      const params = { page, page_size: pageSize };
      
      if (filters.start_date) params.start_date = new Date(filters.start_date).toISOString();
      if (filters.end_date) params.end_date = new Date(filters.end_date).toISOString();
      if (filters.action) params.action = filters.action;
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.actor_user_id) params.actor_user_id = filters.actor_user_id;
      
      const response = await auditAPI.list(params);
      setLogs(response.data.audit_logs);
      setTotal(response.data.total);
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Wait for auth to complete before checking permissions
    if (authLoading) return;
    
    if (canViewAuditLogs) {
      fetchLogs();
    } else {
      setLoading(false);
    }
  }, [canViewAuditLogs, authLoading, page, filters]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchLogs();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.start_date) params.start_date = new Date(filters.start_date).toISOString();
      if (filters.end_date) params.end_date = new Date(filters.end_date).toISOString();
      if (filters.action) params.action = filters.action;
      if (filters.entity_type) params.entity_type = filters.entity_type;
      
      const response = await auditAPI.exportCsv(params);
      
      // Create download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Audit logs exported');
    } catch (err) {
      toast.error('Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setDetailModalOpen(true);
  };

  const clearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      action: '',
      entity_type: '',
      actor_user_id: ''
    });
    setPage(1);
  };

  const hasActiveFilters = Object.values(filters).some(v => v);

  const getActionBadgeColor = (action) => {
    const colors = {
      CREATE: 'bg-green-100 text-green-700 border-green-200',
      UPDATE: 'bg-blue-100 text-blue-700 border-blue-200',
      ARCHIVE: 'bg-red-100 text-red-700 border-red-200',
      LOGIN: 'bg-purple-100 text-purple-700 border-purple-200',
      LOGOUT: 'bg-slate-100 text-slate-700 border-slate-200',
      ROLE_CHANGE: 'bg-amber-100 text-amber-700 border-amber-200',
      PERMISSION_DENIED: 'bg-red-100 text-red-700 border-red-200',
      SEED: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    };
    return colors[action] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  // Wait for auth loading to complete
  if (authLoading) {
    return (
      <Layout title="Audit Logs">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-600" />
        </div>
      </Layout>
    );
  }

  if (!canViewAuditLogs) {
    return (
      <Layout title="Access Denied">
        <div className="flex items-center justify-center py-12">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
              <p className="text-slate-600 mb-4">
                This page is only accessible to Administrators and Compliance Managers.
              </p>
              <Button onClick={() => navigate('/')} variant="outline">
                Go Back
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title="Audit Logs">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-600" />
        </div>
      </Layout>
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Layout title="Audit Logs">
      <div className="space-y-6" data-testid="audit-log-viewer-page">
        {/* Actions Bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button 
              variant={showFilters ? 'default' : 'outline'} 
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-avida-blue hover:bg-avida-blue-800' : ''}
              data-testid="toggle-filters"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <Badge className="ml-2 bg-avida-yellow text-slate-900">Active</Badge>
              )}
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {canExportAuditLogs && (
              <Button variant="outline" onClick={handleExport} disabled={exporting} data-testid="export-csv">
                <Download className="h-4 w-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={refreshing ? 'h-4 w-4 mr-1 animate-spin' : 'h-4 w-4 mr-1'} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                    data-testid="filter-start-date"
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                    data-testid="filter-end-date"
                  />
                </div>
                <div>
                  <Label>Action</Label>
                  <Select value={filters.action} onValueChange={(v) => setFilters({...filters, action: v === 'all' ? '' : v})}>
                    <SelectTrigger data-testid="filter-action">
                      <SelectValue placeholder="All actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All actions</SelectItem>
                      {ACTIONS.map((a) => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Entity Type</Label>
                  <Select value={filters.entity_type} onValueChange={(v) => setFilters({...filters, entity_type: v === 'all' ? '' : v})}>
                    <SelectTrigger data-testid="filter-entity-type">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      {ENTITY_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Actor User ID</Label>
                  <Input
                    value={filters.actor_user_id}
                    onChange={(e) => setFilters({...filters, actor_user_id: e.target.value})}
                    placeholder="User ID..."
                    data-testid="filter-actor-id"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle>Audit Logs ({total})</CardTitle>
            <CardDescription>Complete system activity trail - append only, never deleted</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Timestamp</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Actor</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Action</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Entity</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Reason</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${log.id}`}>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <span className="text-sm text-slate-900">{log.actor_email || 'System'}</span>
                          {log.actor_role && (
                            <span className="text-xs text-slate-500 block">{log.actor_role}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className={getActionBadgeColor(log.action)}>
                          {log.action}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-700">{log.entity_type}</span>
                        {log.entity_id && (
                          <span className="text-xs text-slate-400 block truncate max-w-[150px]">
                            {log.entity_id}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {log.reason ? (
                          <span className="text-sm text-slate-600 truncate block max-w-[200px]">
                            {log.reason}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleViewDetails(log)}
                          data-testid={`view-details-${log.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {logs.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-slate-500">
                        No audit logs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                <span className="text-sm text-slate-600">
                  Page {page} of {totalPages} ({total} total)
                </span>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Audit Log Details</DialogTitle>
            <DialogDescription>
              {selectedLog && new Date(selectedLog.timestamp).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Action</Label>
                  <p className="font-medium">{selectedLog.action}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Entity Type</Label>
                  <p className="font-medium">{selectedLog.entity_type}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Entity ID</Label>
                  <p className="font-mono text-sm">{selectedLog.entity_id || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Actor</Label>
                  <p className="font-medium">{selectedLog.actor_email || 'System'}</p>
                  <p className="text-sm text-slate-500">{selectedLog.actor_role}</p>
                </div>
              </div>
              
              {selectedLog.reason && (
                <div>
                  <Label className="text-slate-500">Reason</Label>
                  <p className="mt-1 p-2 bg-slate-50 rounded">{selectedLog.reason}</p>
                </div>
              )}
              
              {selectedLog.before_json && (
                <div>
                  <Label className="text-slate-500">Before</Label>
                  <pre className="mt-1 p-2 bg-slate-50 rounded text-xs overflow-auto max-h-32">
                    {JSON.stringify(selectedLog.before_json, null, 2)}
                  </pre>
                </div>
              )}
              
              {selectedLog.after_json && (
                <div>
                  <Label className="text-slate-500">After</Label>
                  <pre className="mt-1 p-2 bg-slate-50 rounded text-xs overflow-auto max-h-32">
                    {JSON.stringify(selectedLog.after_json, null, 2)}
                  </pre>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-slate-500">Correlation ID</Label>
                  <p className="font-mono text-xs">{selectedLog.correlation_id || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-slate-500">IP Address</Label>
                  <p className="font-mono">{selectedLog.ip_address || 'N/A'}</p>
                </div>
              </div>
              
              {selectedLog.user_agent && (
                <div>
                  <Label className="text-slate-500">User Agent</Label>
                  <p className="text-xs text-slate-600 truncate">{selectedLog.user_agent}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
}

export default AuditLogViewer;
